import React, { useState } from 'react';
import Input from '../../shared/design-system/components/input';
import Checkbox from '../../shared/design-system/components/atoms/checkbox';
import Switch, { Size as SwitchSize } from '../../shared/design-system/components/atoms/switch';
import Spinner, {
  SpinnerAnimation,
} from '../../shared/design-system/components/atoms/spinner/spinner';
import Pagination from '../../shared/design-system/components/pagination';
import Tag from '../../shared/design-system/components/tag';

const LIST_ITEMS = [
  { name: 'Alice Johnson', email: 'alice.johnson@company.com', tag: 'Active', tagColor: Tag.Color.Green, checked: true },
  { name: 'Bob Smith', email: 'bob.smith@company.com', tag: 'Pending', tagColor: Tag.Color.Gold, checked: false },
  { name: 'Carol Davis', email: 'carol.davis@company.com', tag: 'New', tagColor: Tag.Color.Blue, checked: false },
];

const DesignSystemComponents: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [checkboxChecked2, setCheckboxChecked2] = useState(true);
  const [switchChecked, setSwitchChecked] = useState(false);
  const [switchChecked2, setSwitchChecked2] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  return (
    <>
      {/* 9. Text Patterns */}
      <section id="text-patterns" className="bs-design-system-page__section">
        <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
          Text Patterns
        </h2>
        <p className="regular-1 gray-txt-11 mb-3">
          Common text class combinations used throughout the app.
        </p>

        <div className="bs-design-system-page__component-showcase">
          <div className="bs-design-system-page__pattern-row">
            <span className="regular-1 gray-txt-11 bs-design-system-page__pattern-label">
              Page Heading
            </span>
            <span className="semibold-5 gray-txt-9">Page Title Here</span>
            <code className="bs-design-system-page__code-tag">.semibold-5 .gray-txt-9</code>
          </div>

          <div className="bs-design-system-page__pattern-row">
            <span className="regular-1 gray-txt-11 bs-design-system-page__pattern-label">
              Section Heading
            </span>
            <span className="semibold-3 gray-txt-9">Section Title</span>
            <code className="bs-design-system-page__code-tag">.semibold-3 .gray-txt-9</code>
          </div>

          <div className="bs-design-system-page__pattern-row">
            <span className="regular-1 gray-txt-11 bs-design-system-page__pattern-label">
              Bold Label
            </span>
            <span className="semibold-2 gray-txt-9">Field Label</span>
            <code className="bs-design-system-page__code-tag">.semibold-2 .gray-txt-9</code>
          </div>

          <div className="bs-design-system-page__pattern-row">
            <span className="regular-1 gray-txt-11 bs-design-system-page__pattern-label">
              Body Text
            </span>
            <span className="regular-2 gray-txt-15">
              Default body text for paragraphs and descriptions.
            </span>
            <code className="bs-design-system-page__code-tag">.regular-2 .gray-txt-15</code>
          </div>

          <div className="bs-design-system-page__pattern-row">
            <span className="regular-1 gray-txt-11 bs-design-system-page__pattern-label">
              Caption / Helper
            </span>
            <span className="regular-1 gray-txt-11">Helper text or caption below a field</span>
            <code className="bs-design-system-page__code-tag">.regular-1 .gray-txt-11</code>
          </div>

          <div className="bs-design-system-page__pattern-row">
            <span className="regular-1 gray-txt-11 bs-design-system-page__pattern-label">
              Link Text
            </span>
            <span className="regular-2 blue-txt-6">Click here to learn more</span>
            <code className="bs-design-system-page__code-tag">.regular-2 .blue-txt-6</code>
          </div>

          <div className="bs-design-system-page__pattern-row">
            <span className="regular-1 gray-txt-11 bs-design-system-page__pattern-label">
              Error Text
            </span>
            <span className="regular-1 red-txt-6">This field is required</span>
            <code className="bs-design-system-page__code-tag">.regular-1 .red-txt-6</code>
          </div>

          <div className="bs-design-system-page__pattern-row">
            <span className="regular-1 gray-txt-11 bs-design-system-page__pattern-label">
              Muted / Disabled
            </span>
            <span className="regular-2 gray-txt-6">Disabled or placeholder text</span>
            <code className="bs-design-system-page__code-tag">.regular-2 .gray-txt-6</code>
          </div>
        </div>
      </section>

      {/* 10. Inputs */}
      <section id="inputs" className="bs-design-system-page__section">
        <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
          Inputs
        </h2>

        {/* Variants */}
        <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
          Input Variants
        </h3>
        <div className="bs-design-system-page__input-grid">
          <div>
            <p className="regular-1 gray-txt-11 mb-1">Default</p>
            <Input
              placeholder="Enter text..."
              value={inputValue}
              onChange={(val) => setInputValue(val)}
            />
          </div>
          <div>
            <p className="regular-1 gray-txt-11 mb-1">With Label &amp; Caption</p>
            <Input
              label="Email Address"
              caption="We'll never share your email."
              placeholder="name@example.com"
            />
          </div>
          <div>
            <p className="regular-1 gray-txt-11 mb-1">Error</p>
            <Input
              variant={Input.Variant.Error}
              label="Username"
              caption="Username is already taken."
              placeholder="Enter username"
              value="taken_user"
            />
          </div>
          <div>
            <p className="regular-1 gray-txt-11 mb-1">Warning</p>
            <Input
              variant={Input.Variant.Warning}
              label="Password"
              caption="Password is weak."
              placeholder="Enter password"
              type="password"
              value="abc"
            />
          </div>
          <div>
            <p className="regular-1 gray-txt-11 mb-1">Success</p>
            <Input
              variant={Input.Variant.Success}
              label="Domain"
              caption="Domain is available!"
              placeholder="yourdomain.com"
              value="mysite.com"
            />
          </div>
          <div>
            <p className="regular-1 gray-txt-11 mb-1">Disabled</p>
            <Input
              label="Read Only"
              placeholder="Cannot edit"
              value="Locked value"
              disabled
            />
          </div>
        </div>

        {/* Sizes */}
        <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
          Input Sizes
        </h3>
        <div className="bs-design-system-page__input-grid">
          <div>
            <p className="regular-1 gray-txt-11 mb-1">Small</p>
            <Input size={Input.Size.Small} placeholder="Small input" />
          </div>
          <div>
            <p className="regular-1 gray-txt-11 mb-1">Medium (Default)</p>
            <Input size={Input.Size.Medium} placeholder="Medium input" />
          </div>
          <div>
            <p className="regular-1 gray-txt-11 mb-1">Large</p>
            <Input size={Input.Size.Large} placeholder="Large input" />
          </div>
        </div>

        {/* With Icons */}
        <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
          Input with Icon
        </h3>
        <div className="bs-design-system-page__input-grid">
          <div>
            <p className="regular-1 gray-txt-11 mb-1">Left Icon</p>
            <Input
              placeholder="Search..."
              icons={[{ place: Input.IconPlace.Left, identifier: 'search' }]}
            />
          </div>
          <div>
            <p className="regular-1 gray-txt-11 mb-1">Right Icon</p>
            <Input
              placeholder="Enter value"
              icons={[{ place: Input.IconPlace.Right, identifier: 'calendar' }]}
            />
          </div>
        </div>
      </section>

      {/* 11. Checkboxes */}
      <section id="checkboxes" className="bs-design-system-page__section">
        <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
          Checkboxes
        </h2>
        <div className="d-flex flex-wrap gap-24 align-items-start">
          <div className="d-flex flex-column gap-12">
            <p className="regular-1 gray-txt-11 mb-0">States</p>
            <Checkbox
              label="Unchecked"
              checked={checkboxChecked}
              onChange={(checked) => setCheckboxChecked(checked)}
            />
            <Checkbox
              label="Checked"
              checked={checkboxChecked2}
              onChange={(checked) => setCheckboxChecked2(checked)}
            />
            <Checkbox label="Indeterminate" checked intermediate />
            <Checkbox label="Disabled" disabled />
            <Checkbox label="Disabled Checked" checked disabled />
          </div>
        </div>
      </section>

      {/* 12. Switches */}
      <section id="switches" className="bs-design-system-page__section">
        <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
          Switches
        </h2>
        <div className="d-flex flex-wrap gap-24 align-items-start">
          <div className="d-flex flex-column gap-16">
            <div>
              <p className="regular-1 gray-txt-11 mb-1">Default</p>
              <Switch
                checked={switchChecked}
                onChange={(checked) => setSwitchChecked(checked)}
                label="Off"
              />
            </div>
            <div>
              <p className="regular-1 gray-txt-11 mb-1">Checked</p>
              <Switch
                checked={switchChecked2}
                onChange={(checked) => setSwitchChecked2(checked)}
                label="On"
              />
            </div>
            <div>
              <p className="regular-1 gray-txt-11 mb-1">Small Size</p>
              <Switch checked size={SwitchSize.Small} label="Small" />
            </div>
            <div>
              <p className="regular-1 gray-txt-11 mb-1">Disabled</p>
              <Switch disabled label="Disabled" />
            </div>
            <div>
              <p className="regular-1 gray-txt-11 mb-1">Disabled Checked</p>
              <Switch checked disabled label="Disabled On" />
            </div>
          </div>
        </div>
      </section>

      {/* 13. Spinners */}
      <section id="spinners" className="bs-design-system-page__section">
        <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
          Spinners
        </h2>
        <div className="d-flex flex-wrap gap-24 align-items-center">
          <div className="d-flex flex-column align-items-center gap-8">
            <Spinner animation={SpinnerAnimation.Border} />
            <span className="regular-1 gray-txt-11">Border</span>
          </div>
          <div className="d-flex flex-column align-items-center gap-8">
            <Spinner animation={SpinnerAnimation.Grow} />
            <span className="regular-1 gray-txt-11">Grow</span>
          </div>
        </div>
      </section>

      {/* 14. Tags */}
      <section id="tags" className="bs-design-system-page__section">
        <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
          Tags
        </h2>

        <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
          Tag Colors
        </h3>
        <div className="d-flex flex-wrap gap-8 mb-3">
          <Tag value="Geekblue" color={Tag.Color.Geekblue} />
          <Tag value="Blue" color={Tag.Color.Blue} />
          <Tag value="Red" color={Tag.Color.Red} />
          <Tag value="Volcano" color={Tag.Color.Volcano} />
          <Tag value="Orange" color={Tag.Color.Orange} />
          <Tag value="Gold" color={Tag.Color.Gold} />
          <Tag value="Lime" color={Tag.Color.Lime} />
          <Tag value="Green" color={Tag.Color.Green} />
          <Tag value="Cyan" color={Tag.Color.Cyna} />
          <Tag value="Magenta" color={Tag.Color.Magenta} />
        </div>

        <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
          Tag Variants
        </h3>
        <div className="d-flex flex-wrap gap-8">
          <Tag value="Default Tag" />
          <Tag value="Removable Tag" variant={Tag.Variant.Remove} color={Tag.Color.Blue} />
          <Tag value="Add Tag" variant={Tag.Variant.Add} color={Tag.Color.Geekblue} />
        </div>
      </section>

      {/* 15. Pagination */}
      <section id="pagination" className="bs-design-system-page__section">
        <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
          Pagination
        </h2>
        <p className="regular-1 gray-txt-11 mb-3">
          Navigate between pages. Current page: {currentPage}
        </p>
        <Pagination
          totalPages={10}
          currentPage={currentPage}
          onChange={(page) => setCurrentPage(page)}
        />
      </section>

      {/* 16. Card Layout */}
      <section id="cards" className="bs-design-system-page__section">
        <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
          Card Layouts
        </h2>
        <div className="bs-design-system-page__card-grid">
          <div className="bs-border-radius-8 gray-19 p-3">
            <h3 className="semibold-3 gray-txt-9 mb-2">Default Card</h3>
            <p className="regular-2 gray-txt-15 mb-0">
              Uses .bs-border-radius-8 .gray-19 with padding.
            </p>
          </div>

          <div className="bs-design-system-page__card--bordered p-3">
            <h3 className="semibold-3 gray-txt-9 mb-2">Bordered Card</h3>
            <p className="regular-2 gray-txt-15 mb-0">
              Card with border and white background.
            </p>
          </div>

          <div className="bs-design-system-page__card--elevated p-3">
            <h3 className="semibold-3 gray-txt-9 mb-2">Elevated Card</h3>
            <p className="regular-2 gray-txt-15 mb-0">
              Card with shadow for popover-like appearance.
            </p>
          </div>
        </div>
      </section>

      {/* 17. Common Patterns */}
      <section id="patterns" className="bs-design-system-page__section">
        <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
          Common Patterns
        </h2>

        {/* Form Layout */}
        <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
          Form Layout
        </h3>
        <div className="bs-design-system-page__card--bordered p-4">
          <h3 className="semibold-3 gray-txt-9 mb-3">Create Account</h3>
          <div className="d-flex flex-column gap-16">
            <Input label="Full Name" placeholder="John Doe" />
            <Input label="Email" placeholder="john@example.com" type="email" />
            <Input
              label="Password"
              placeholder="Enter password"
              type="password"
              caption="Minimum 8 characters."
            />
            <Checkbox label="I agree to the terms and conditions" />
            <div className="d-flex gap-8">
              <button type="button" className="btn btn-primary bs-border-radius-4">Submit</button>
              <button type="button" className="btn btn-default bs-border-radius-4">Cancel</button>
            </div>
          </div>
        </div>

        {/* Header Pattern */}
        <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
          Page Header with Actions
        </h3>
        <div className="bs-design-system-page__card--bordered p-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="semibold-4 gray-txt-9 mb-0">Prospects</h2>
              <p className="regular-1 gray-txt-11 mb-0">Manage your prospect list</p>
            </div>
            <div className="d-flex gap-8">
              <Input
                size={Input.Size.Small}
                placeholder="Search..."
                icons={[{ place: Input.IconPlace.Left, identifier: 'search' }]}
              />
            </div>
          </div>
        </div>

        {/* List Item Pattern */}
        <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
          List Items
        </h3>
        <div className="bs-design-system-page__card--bordered">
          {LIST_ITEMS.map((item, i) => (
            <div
              key={item.name}
              className={`d-flex align-items-center justify-content-between p-3${
                i < LIST_ITEMS.length - 1 ? ' bs-design-system-page__list-item' : ''
              }`}
            >
              <div className="d-flex align-items-center gap-12">
                <Checkbox checked={item.checked} />
                <div>
                  <p className="semibold-2 gray-txt-9 mb-0">{item.name}</p>
                  <p className="regular-1 gray-txt-11 mb-0">{item.email}</p>
                </div>
              </div>
              <Tag value={item.tag} color={item.tagColor} />
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default DesignSystemComponents;
